<template>
  <el-dropdown
    trigger="click"
    @command="toggleValue"
    :hide-on-click="false"
    ref="menudrop"
    class="transfer-dropdown"
    v-if="useMenu"
    @visible-change="stayOpen"
    :persistent="true"
  >
    <span class="el-dropdown-link"></span>
    <template #dropdown>
      <el-dropdown-menu
        slot="dropdown"
        class="transfer-dropdown-menu"
        style="height: 80vh"
      >
        <el-dropdown-item
          divided
          @click.prevent.stop="toggleCallControl"
          :command="'dialpad'"
          v-bind:key="'callControl'"
          >Dial Pad</el-dropdown-item
        >
        <call-control
          v-if="showCallControl"
          v-on:accept="showCallControl = !showCallControl"
          :callId="callId"
          v-on:dial="doDial"
          :in-transfer-menu="true"
        ></call-control>
        <el-dropdown-item
          divided
          disabled
          v-if="getStandardQueues.length && !showCallControl"
          ><u>Standard Queues</u></el-dropdown-item
        >
        <el-dropdown-item
          v-for="queue in getStandardQueues"
          v-bind:key="'queue:' + queue.id"
          :command="'queue:' + queue.id"
          >{{ queue.name }}</el-dropdown-item
        >

        <el-dropdown-item
          divided
          disabled
          v-if="getAgentQueues.length && !showCallControl"
          ><u>Agent Queues</u></el-dropdown-item
        >
        <el-dropdown-item
          v-for="queue in getAgentQueues"
          v-bind:key="'queue:' + queue.id"
          :command="'queue:' + queue.id"
          :class="queue.class"
          >{{ queue.name }}</el-dropdown-item
        >

        <el-dropdown-item
          divided
          disabled
          v-if="getDirectDials.length && !showCallControl"
          ><u>Direct Dials</u></el-dropdown-item
        >
        <el-dropdown-item
          v-for="dial in getDirectDials"
          v-bind:key="'dial:' + dial.id"
          :command="'directDial:' + dial.id"
          style="display: flex; justify-content: space-between"
          >{{ dial.name }}
          <v-btn
            @click.native.prevent.stop="warmTransfer('directDial:' + dial.id)"
            size="x-small"
            color="orange"
          >
            <v-icon>mdi-axis-z-arrow</v-icon>
          </v-btn>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
  <el-select
    label="Select Queue"
    v-model="selected"
    @change="toggleValue()"
    filterable
    v-if="!useMenu"
  >
    <el-option-group v-if="getStandardQueues.length"
      >Standard Queues
      <el-option
        v-for="queue in getStandardQueues"
        v-bind:key="queue.id"
        :value="queue.id"
        :label="queue.name"
      ></el-option>
    </el-option-group>

    <el-option-group v-if="getDirectDials.length"
      >Direct Dials
      <el-option
        v-for="dial in getDirectDials"
        v-bind:key="dial.id"
        :value="dial.id"
        :label="dial.name"
      ></el-option>
    </el-option-group>

    <el-option-group v-if="getAgentQueues.length"
      >Agent Queues
      <el-option
        v-for="queue in getAgentQueues"
        v-bind:key="queue.id"
        :value="queue.id"
        :label="queue.name"
      ></el-option>
    </el-option-group>
  </el-select>
</template>

<script setup>
import { reactive, computed, ref, watch, onMounted } from 'vue';
import { mapActions } from 'vuex';
import { useStore } from 'vuex';

import CallControl from 'src/components/UIComponents/CallControl';

// Component bits

const name = 'transfer-select';
const store = useStore();

const emit = defineEmits(['update:value']);

// Data
// import type { DropdownInstance } from 'element-plus'
const menudrop = ref(null);
const selected = ref(0);
const showCallControl = ref(false);
const orderedQueues = ref([]);

// Computed
const getQueues = computed(() => {
  return store.getters['queues/getQueues'];
});
const getDirectDials = computed(() => {
  return store.getters['voice/getDirectDials'];
});
const getActiveAgents = computed(() => {
  return store.getters['realtime/getActiveAgents'];
});
const getAgentQueues = computed(() => {
  const agentIds = getActiveAgents.value
    .filter((a) => a.status == 'active')
    .map((a) => a.user_id);
  if (
    store.getters['appConfig/getAppConfig'].callCenter?.activeAgentTrans ==
    'filter'
  ) {
    // modeling is a little different for agent queues
    // Assume there is one agent object
    return getQueues.value.filter((q) => {
      return (
        q.queue_type == 'agent' &&
        q.agents.length == 1 &&
        agentIds.includes(q.agents[0].id)
      );
    });
  }
  if (
    store.getters['appConfig/getAppConfig'].callCenter?.activeAgentTrans ==
      'disabled' ||
    store.getters['appConfig/getAppConfig'].callCenter?.activeAgentTrans ==
      'lowlight'
  ) {
    const q = getQueues.value
      .filter((q) => q.queue_type == 'agent' && q.agents.length == 1)
      .map((q1) => {
        return {
          ...q1,
          class: agentIds.includes(q1.agents[0].id) ? null : 'show-disabled',
          disabled:
            store.getters['appConfig/getAppConfig'].callCenter
              ?.activeAgentTrans == 'disabled'
              ? true
              : false,
        };
      });
    return q;
  }

  return getQueues.value.filter((q) => q.queue_type == 'agent');
});
const getStandardQueues = computed(() => {
  return getQueues.value.filter((q) => q.queue_type == 'standard');
});

// Props
const props = defineProps({
  value: {
    type: String,
    required: false,
  },
  reload: {
    type: Boolean,
    required: false,
    default: () => {
      return false;
    },
  },
  useMenu: {
    type: Boolean,
    required: false,
    default: () => {
      return false;
    },
  },
  callId: {
    type: Number,
    required: true,
    description: 'Current Call that is being controlled',
  },
});

// Methods
async function fetchQueues() {
  return store.dispatch('queues/fetchQueues');
}
async function fetchDirectDials() {
  return store.dispatch('voice/fetchDirectDials');
}

// const menuDrop = ref<DropdownInstance>();
function toggleValue(value) {
  // Set internal when using menu
  if (props.useMenu) {
    selected.value = value;
  }
  // It passed the value to the children so it needs to be false to prevent child errors
  if (value === 'dialpad') {
    menudrop.value.handleClose();
    return undefined;
  }
  emit('update:value', String(selected.value));
  return undefined;
}
function stayOpen() {
  menudrop.value.handleOpen();
  return false;
}
function toggleCallControl(value) {
  showCallControl.value = !showCallControl.value;
  return false;
}
function doDial(digits) {
  emit('update:value', 'dial:' + digits);
}
function warmTransfer(directId) {
  showCallControl.value = !showCallControl.value;
  emit('update:value', String(directId) + ':warm');
  return undefined;
}

/*
   Not sure we need this
  watch(props.value, (n) => {
    selected.value = n;
  });
  */

// Created

onMounted(async () => {
  // Force it open on show
  if (props.useMenu && menudrop) {
    menudrop.value.handleOpen();
  }
  if (props.value) {
    selected.value = parseInt(props.value);
  }
  if (props.reload || !getQueues.value.length) {
    await fetchQueues();
  }
  if (props.reload || !getDirectDials.value.length) {
    await fetchDirectDials();
  }
});
</script>
<style scoped>
.transfer-dropdown {
  position: relative;
  min-width: 1625px; /* Needs to be large than all sub components above */
  left: -200px;
  top: 40px;
  .transfer-dropdown-menu {
    overflow-y: scroll;
    overflow-x: clip;
  }
}
</style>
<style>
.show-disabled {
  color: var(--el-text-color-disabled) !important;
}
</style>
